// frontend/src/utils/onboarding.js
//
// From the name generators to a ready-to-use dashboard (Growth round, T24).
//
//   1. The generator pages remember what the visitor made: the name they
//      picked, the style it was made in and their niche or AI vibe
//      (NAME_CONTEXT_KEY, saveNameContext).
//   2. The sign-up page (/register?claim=…&from=…&kit=…) and the log-in page
//      (same query) carry the claim, shortlist and kit id to the server. After
//      a claim-flow sign-in they leave a ONE-TIME hand-off (WELCOME_KEY) and
//      open AI Studio: welcomeFrom() builds it.
//   3. The dashboard shows the green welcome bar from `bar` once, and AI
//      Studio opens on the 'personal' goal prefilled from `prefill`, in
//      whichever flow (chat or form) is the default. Each part is removed
//      from storage the first time it is shown, so neither comes back.
//
// Everything stays in this browser's localStorage; nothing here is sent
// anywhere except the claim, shortlist and kit id the server already takes.

import { claimNameFor, isClaimable, readShortlist } from './nameClaim'

export const NAME_CONTEXT_KEY = 'gs_name_context'
export const WELCOME_KEY = 'gs_welcome'
export const STUDIO_PATH = '/dashboard/studio/bulk'
export const PERSONAL_GOAL = 'personal'

const CONTEXT_TTL_MS = 7 * 24 * 60 * 60 * 1000   // a week-old visit is still "theirs"
const WELCOME_TTL_MS = 60 * 60 * 1000            // the hand-off is for the next page load
const FIELD_MAX = 200                            // the guided chat keeps 200 characters per answer
const KIT_ID = /^[A-Za-z0-9_-]{1,64}$/
const SLUG = /^[a-z0-9-]{1,80}$/

const text = (v, max = FIELD_MAX) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '')

function readJson (key) {
  try { return JSON.parse(window.localStorage.getItem(key) || 'null') } catch { return null }
}
function writeJson (key, value) {
  try {
    if (value) window.localStorage.setItem(key, JSON.stringify(value))
    else window.localStorage.removeItem(key)
  } catch { /* storage blocked: the visitor just sees the plain pages */ }
}

/* ── 1. what the generator pages know ──────────────────────────────────── */

/**
 * { name?, style?, styleLabel?, niche?, vibe?, from? } — `niche` is the quick
 * generator's second field, `vibe` the 'AI names from your vibe' description.
 */
export function saveNameContext (ctx) {
  const clean = {
    name: text(ctx && ctx.name, 60),
    style: text(ctx && ctx.style, 30),
    styleLabel: text(ctx && ctx.styleLabel, 60),
    niche: text(ctx && ctx.niche, 60),
    vibe: text(ctx && ctx.vibe, 120),
    from: text(ctx && ctx.from, 80),
    at: Date.now(),
  }
  writeJson(NAME_CONTEXT_KEY, clean)
  return clean
}

export function readNameContext () {
  const c = readJson(NAME_CONTEXT_KEY)
  if (!c || typeof c !== 'object' || !(Date.now() - Number(c.at) < CONTEXT_TTL_MS)) return null
  return c
}

/* ── 2. sign-up / log-in from the claim flow ──────────────────────────── */

/* /register or /login ?claim=<link-in-bio name>&from=<page>&kit=<AI profile kit id>,
   plus the ♡ shortlist kept in this browser. */
export function claimFlowFrom (search) {
  const q = new URLSearchParams(search || '')
  const claim = claimNameFor(q.get('claim') || '')
  const kit = q.get('kit') || ''
  const from = q.get('from') || ''
  return {
    claim: isClaimable(claim) ? claim : '',
    kitId: KIT_ID.test(kit) ? kit : '',
    from: SLUG.test(from) ? from : '',
    shortlist: readShortlist(),
  }
}

/* Arrived from a generator page (a Claim, 'Save it' or 'Unlock it free' link).
   A shortlist alone does not count: a plain /register or /login stays plain. */
export const inClaimFlow = flow => !!(flow && (flow.claim || flow.kitId || flow.from))

/** The extra sign-in fields the server takes from the claim flow. */
export function claimPayload (flow) {
  return {
    ...(flow.claim ? { claimedUsername: flow.claim } : {}),
    ...(flow.shortlist.length ? { shortlist: flow.shortlist } : {}),
    ...(flow.kitId ? { kitId: flow.kitId } : {}),
  }
}

/** Keeps claim / from / kit when moving between /register and /login. */
export function carryQuery (search) {
  const q = new URLSearchParams(search || '')
  const out = new URLSearchParams()
  for (const k of ['claim', 'from', 'kit']) if (q.get(k)) out.set(k, q.get(k))
  const s = out.toString()
  return s ? `?${s}` : ''
}

/** '#cozyVibes' → 'cozyVibes'; the first few, as a list a person would write. */
function topicsFromHashtags (tags, n = 4) {
  return (Array.isArray(tags) ? tags : [])
    .map(t => (typeof t === 'string' ? t.replace(/^#+/, '').trim() : ''))
    .filter(Boolean).slice(0, n).join(', ')
}

/** 'instagram-name-generator' → 'name generator'; '…-username-generator' → 'username generator'. */
export function sourceLabel (from) {
  return /username-generator$/.test(String(from || '')) ? 'username generator' : 'name generator'
}

/**
 * AI Studio's prefill for the 'personal' goal ("Grow my personal account"):
 *   accountName  the handle they picked (or the claimed name)
 *   vibe         the style it was made in ('Soft and aesthetic' → 'soft and aesthetic')
 *   topics       the niche, else their AI vibe description, else the kit's hashtags
 * Missing parts stay empty; the form and the chat then simply ask for them.
 */
export function studioPrefillFrom ({ claim, context, kit, from } = {}) {
  const ctx = context || {}
  const picked = text(ctx.name, 60).replace(/^@+/, '')
  const handle = picked && (!claim || claimNameFor(picked) === claim) ? picked : (claim || picked)
  const fields = {}
  if (handle) fields.accountName = `@${handle}`
  const vibe = text(ctx.styleLabel).toLowerCase()
  if (vibe) fields.vibe = vibe
  const topics = text(ctx.niche) || text(ctx.vibe) || text(topicsFromHashtags(kit && kit.hashtags))
  if (topics) fields.topics = topics
  if (!Object.keys(fields).length) return null
  return { goal: PERSONAL_GOAL, fields, source: sourceLabel(from || ctx.from) }
}

/**
 * The one-time hand-off after a claim-flow sign-in.
 *   bar      { username, status, message? } for the welcome bar
 *   prefill  studioPrefillFrom(…) for AI Studio
 * `claimResult` is the server's `claim` ({ username, status, message? }).
 */
export function welcomeFrom ({ flow, claimResult, context, kit, from } = {}) {
  const claim = (claimResult && claimResult.username) || (flow && flow.claim) || ''
  const bar = claimResult && claimResult.username
    ? { username: claimResult.username, status: claimResult.status || '', message: text(claimResult.message, 300) }
    : null
  return {
    bar,
    prefill: studioPrefillFrom({ claim, context: context === undefined ? readNameContext() : context, kit, from }),
    at: Date.now(),
  }
}

export function saveWelcome (welcome) {
  writeJson(WELCOME_KEY, welcome && (welcome.bar || welcome.prefill) ? welcome : null)
}

/**
 * After a claim-flow sign-up or log-in: leave the hand-off for the dashboard
 * (the caller then opens STUDIO_PATH with goTo). The kit is only fetched
 * (fetchKit(kitId), logged in by now) when the page gave no niche or vibe to
 * fill "What you post about" with. Never throws.
 */
export async function saveClaimWelcome ({ flow, claimResult, fetchKit }) {
  const context = readNameContext()
  let kit = null
  if (flow.kitId && fetchKit && !(context && (context.niche || context.vibe))) {
    try { kit = await fetchKit(flow.kitId) } catch { kit = null }
  }
  saveWelcome(welcomeFrom({ flow, claimResult, context, kit, from: flow.from }))
}

/* ── 3. the dashboard side ─────────────────────────────────────────────── */

function readWelcome () {
  const w = readJson(WELCOME_KEY)
  if (!w || typeof w !== 'object') return null
  if (!(Date.now() - Number(w.at) < WELCOME_TTL_MS)) { writeJson(WELCOME_KEY, null); return null }
  return w
}

// Reading never removes; the use* below remove after the first render, so a
// double render (React StrictMode) still sees the value once it is shown.
function forget (part) {
  const w = readJson(WELCOME_KEY)
  if (!w || typeof w !== 'object') return
  delete w[part]
  writeJson(WELCOME_KEY, w.bar || w.prefill ? w : null)
}

export function peekWelcomeBar () {
  const w = readWelcome()
  const bar = w && w.bar
  return bar && typeof bar.username === 'string' && bar.username ? bar : null
}
export const forgetWelcomeBar = () => forget('bar')

export function peekStudioPrefill () {
  const w = readWelcome()
  const p = w && w.prefill
  if (!p || p.goal !== PERSONAL_GOAL || !p.fields || typeof p.fields !== 'object') return null
  const fields = {}
  for (const k of ['accountName', 'vibe', 'topics', 'audience']) {
    const v = text(p.fields[k])
    if (v) fields[k] = v
  }
  return Object.keys(fields).length ? { goal: PERSONAL_GOAL, fields, source: text(p.source, 40) || 'name generator' } : null
}
export const forgetStudioPrefill = () => forget('prefill')

/** A full page load, so the dashboard starts with the new session. */
export function goTo (path) {
  window.location.assign(path)
}
